import { MutableRefObject, useEffect, useEffectEvent } from 'react';

const VISIBLE_SHARE = 0.5;

const viewedRatio = (targetHeight: number, viewportHeight: number) =>
  targetHeight > 0 && viewportHeight > 0
    ? Math.min(VISIBLE_SHARE, (viewportHeight * VISIBLE_SHARE) / targetHeight)
    : VISIBLE_SHARE;

const useViewportEnter = (
  targetRef: MutableRefObject<Element | null>,
  onEnter: () => void,
  resetKey?: string
) => {
  const handleEnter = useEffectEvent(onEnter);

  useEffect(() => {
    const target = targetRef.current;
    if (!target || typeof window.IntersectionObserver === 'undefined') {
      return undefined;
    }

    // Fires once per resetKey (page view); scrolling back to the target is not a new view.
    let entered = false;
    let observedRatio: number | null = null;
    let observer: IntersectionObserver | null = null;

    const sync = () => {
      if (entered) return;
      const ratio = viewedRatio(
        target.getBoundingClientRect().height,
        document.documentElement.clientHeight
      );
      if (ratio === observedRatio) return;

      observedRatio = ratio;
      observer?.disconnect();
      const next = new window.IntersectionObserver(
        (entries) => {
          const [threshold] = next.thresholds;
          if (
            entered ||
            !entries.some(
              ({ intersectionRatio }) => intersectionRatio >= threshold
            )
          ) {
            return;
          }
          entered = true;
          next.disconnect();
          observer = null;
          handleEnter();
        },
        { threshold: ratio }
      );
      observer = next;
      next.observe(target);
    };

    sync();
    window.addEventListener('resize', sync);
    const resizeObserver =
      typeof window.ResizeObserver === 'undefined'
        ? null
        : new window.ResizeObserver(sync);
    resizeObserver?.observe(target);

    return () => {
      window.removeEventListener('resize', sync);
      resizeObserver?.disconnect();
      observer?.disconnect();
    };
  }, [targetRef, resetKey]);
};

export default useViewportEnter;
